import { LeftOutlined } from "@ant-design/icons";
import { Button, Flex, Pagination, Skeleton, Typography } from "antd";
import type React from "react";
import { useNavigate } from "react-router-dom";
import EmptyStateNote from "../../components/EmptyState/EmptyStateNote.component";
import NotesList from "../../components/Notes/NotesList.component";
import HowToTell from "../../components/Tutorial/HowToTell.component";
import Tutorial from "../../components/Tutorial/Tutorial.component";
import { useRootPath } from "../../hooks/app.hook";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { useScrollToTop } from "../../hooks/ui-hook";
import { setLoadingNotesState, setNotesViewVisible, setSelectedClient } from "../../slices/appStateSlice";
import { setNotesDetails, setRecordingId, setTotalNotes } from "../../slices/notesSlice";
import type { TNotes } from "../../types/index.type";
import { EventType, trackEvent } from "../../utils/analytics";
import { NOTES_PAGE_SIZE } from "../../utils/constants";
import { eventEmitter } from "../../utils/eventEmitter";
import NoteGenerating from "../notes/NoteGenerating.component";
import Notes from "../notes/notes.component";
import styles from "./home.module.scss";

type TNotesSectionProps = {
  notes: TNotes[];
  setNotes: (notes: TNotes[]) => void;
  fetchNotes: (pageNumber?: number) => void;
  demoNotesRef?: React.RefObject<HTMLDivElement>;
};

/**
 * Center-pane content: the notes LIST is front and center (Superdoc sits in
 * the right pane). With no patient selected the list shows every note; with a
 * patient selected it shows only that patient's notes. Opening a note swaps the
 * list for the single-note viewer (which has its own Back button), and a
 * regenerating version shows the generating spinner.
 */
const NotesSection = ({ notes, setNotes, fetchNotes, demoNotesRef }: TNotesSectionProps) => {
  const componentName = "home";
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const scrollToTop = useScrollToTop();
  const { rootPath } = useRootPath();
  const {
    selectedClient,
    currentPage,
    isNotesViewVisible,
    loadingNotes,
    isDemoPath,
    isMobilePath,
    noteGenerating,
    superdocLayout,
  } = useAppSelector((state) => state.appState);
  const { totalNotes } = useAppSelector((state) => state.notes);
  const { clients, sharedClients } = useAppSelector((state) => state.client);

  const showTutorial = totalNotes === 0 && !loadingNotes && !selectedClient;

  const navigateToNotesDetail = (item: TNotes) => {
    setNotes(notes.map((note) => (note.id === item.id ? { ...note, is_read: true } : note)));
    if (item.error_status !== 0 || item.status !== 1) {
      return;
    }
    trackEvent(EventType.SELECT_NOTE, { recordingId: item.id });
    dispatch(setNotesDetails(item));
    dispatch(setLoadingNotesState(true));
    dispatch(setNotesViewVisible(true));
    dispatch(setRecordingId(item.id));

    // If the note belongs to a client, bind it so the note opens in that
    // client's context (and Superdoc picks up the patient). Resolve from the
    // loaded client lists — an off-list note just opens in place.
    if (item.client) {
      const client = clients.find((c) => c.id === item.client) || sharedClients.find((c) => c.id === item.client);
      if (client) {
        dispatch(setSelectedClient(client));
        if (client.uuid) {
          navigate(`${rootPath}/clients/${client.uuid}`);
        }
      }
    }
    scrollToTop();
  };

  const clearSelection = () => {
    dispatch(setLoadingNotesState(true));
    dispatch(setTotalNotes(0));
    dispatch(setSelectedClient(null));
    navigate(rootPath);
    eventEmitter.emit("refreshNotes");
  };

  // A regenerating version takes precedence — show the spinner until it swaps
  // to the finished note.
  if (noteGenerating) {
    return <NoteGenerating />;
  }
  if (showTutorial || isDemoPath) {
    return <Tutorial />;
  }
  if (isNotesViewVisible) {
    return <Notes />;
  }
  // superdoc-center layout: the notes LIST lives in the sidebar, so the center
  // pane is a single-note viewer only — nothing to show when no note is open.
  if (superdocLayout === "superdoc-center") {
    return null;
  }

  return (
    <>
      {selectedClient && (
        <Flex justify="space-between" style={{ marginBottom: 12 }}>
          <Button
            className={`${styles[`${componentName}-back`]} secondary-button`}
            onClick={clearSelection}
            icon={<LeftOutlined />}
          >
            Back to all notes
          </Button>
        </Flex>
      )}
      {loadingNotes ? (
        <>
          <Skeleton active />
          <Skeleton active />
        </>
      ) : notes?.length ? (
        <div style={{ overflowAnchor: "none" }}>
          {selectedClient && (
            <Typography style={{ fontSize: 18, fontWeight: 600, color: "#111827", margin: "0 0 12px" }}>
              {selectedClient.name}'s Notes
            </Typography>
          )}
          {notes.map((item, index) => (
            <NotesList
              noteRef={demoNotesRef}
              item={item}
              key={item.id}
              index={index}
              navigateToNotesDetail={navigateToNotesDetail}
              fetchNotes={fetchNotes}
            />
          ))}
          <Pagination
            onChange={(page) => fetchNotes(page)}
            current={currentPage}
            pageSize={NOTES_PAGE_SIZE}
            total={totalNotes}
            showSizeChanger={false}
            style={{ textAlign: "center" }}
          />
        </div>
      ) : (
        <>
          <EmptyStateNote />
          {!isMobilePath && <HowToTell />}
        </>
      )}
    </>
  );
};

export default NotesSection;
